import { useAuth } from "@clerk/react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ArrowLeft, ArrowRight, Play, Sparkles, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { putTourState } from "../../lib/app-state-api";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import {
  PLATFORM_TOUR_STEPS,
  clearTourSessionDismiss,
  isTourSessionDismissed,
  loadTourState,
  markTourSessionDismissed,
  saveTourState,
  type PlatformTourStep,
  type TourState,
} from "./platform-tour";

export { PLATFORM_TOUR_STEPS, presentTourSteps } from "./platform-tour";
export type { PlatformTourStep, TourState, TourStatus } from "./platform-tour";

// Minimum gap between intermediate in_progress backend writes.
// Rapid Back/Next clicks collapse into at most one write per gap window.
const IN_PROGRESS_WRITE_GAP_MS = 800;

type Props = {
  ready: boolean;
  onComplete?: () => void;
  /**
   * Bootstrap tour state decoded from /api/users/me app_state.
   * - null/undefined → treat as missing backend state (→ not_started, auto-open allowed)
   * - {status:"dismissed"|"completed"} → suppress auto-open permanently
   * - {status:"in_progress"} → show resume pill at saved step, NOT force-open
   * - {status:"not_started"} → auto-open (unless session-dismissed)
   */
  bootstrapTourState?: TourState | null;
  /**
   * Code-review fix (M3 iteration 2, finding 5). The step list to walk —
   * defaults to every step. Callers (`HomeView`) pass `presentTourSteps(...)`
   * so a step whose target block isn't on the current Home render is
   * skipped rather than shown pointing at nothing. `step` indexes into
   * THIS list.
   */
  steps?: readonly PlatformTourStep[];
};

type InternalState = TourState;
type InitialSource = "backend" | "local" | "default";
type ResolvedInitialState = {
  source: InitialSource;
  state: InternalState;
};

function isTerminalState(state: InternalState): boolean {
  return state.status === "dismissed" || state.status === "completed";
}

/**
 * Determine the initial tour state:
 * 1. Bootstrap backend state (authoritative when present)
 * 2. localStorage fallback (same-browser write-failure cache)
 * 3. Default not_started
 *
 * Backend wins on conflict.
 */
function resolveInitialState(
  bootstrapTourState?: TourState | null,
): ResolvedInitialState {
  if (bootstrapTourState != null) {
    return { source: "backend", state: bootstrapTourState };
  }
  const stored = loadTourState();
  if (stored) {
    return { source: "local", state: stored };
  }
  return { source: "default", state: { status: "not_started", step: 0 } };
}

/**
 * Determine whether the modal should auto-open on mount.
 *
 * Backend wins on conflict — if bootstrap says dismissed/completed,
 * do NOT auto-open even if localStorage says not_started.
 * Only missing backend state permits localStorage to act as fallback.
 */
function resolveInitialOpen(
  state: InternalState,
  bootstrapTourState?: TourState | null,
): boolean {
  if (bootstrapTourState != null) {
    const { status } = bootstrapTourState;
    if (status === "dismissed" || status === "completed") {
      return false;
    }
    // in_progress → show resume pill, not the modal
    if (status === "in_progress") {
      return false;
    }
    // not_started from backend → auto-open (unless session-dismissed)
    return !isTourSessionDismissed();
  }

  // No bootstrap state — use resolved initial state.
  const { status } = state;
  if (status === "dismissed" || status === "completed" || status === "in_progress") {
    return false;
  }
  // not_started with no backend state → auto-open allowed
  return !isTourSessionDismissed();
}

export function PlatformTourController({
  ready,
  onComplete,
  bootstrapTourState,
  steps = PLATFORM_TOUR_STEPS,
}: Props) {
  // Self-source the Clerk token resolver — the controller always renders inside
  // ClerkProvider. getToken() resolves null when there is no active session
  // (e.g. the DEV overview preview), which keeps writes localStorage-only.
  const { getToken } = useAuth();

  // Compute initial tour state once — `resolveInitialState` reads localStorage.
  // A single combined initializer avoids a second redundant storage read.
  const [{ initialState, initialOpen, initialSource }] = useState(() => {
    const resolved = resolveInitialState(bootstrapTourState);
    return {
      initialSource: resolved.source,
      initialState: resolved.state,
      initialOpen: resolveInitialOpen(resolved.state, bootstrapTourState),
    };
  });
  const [state, setState] = useState<InternalState>(initialState);
  const [isOpen, setIsOpen] = useState<boolean>(initialOpen);

  // Throttle timer ref for in_progress writes.
  const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track the latest in_progress state we need to flush.
  const pendingInProgressRef = useRef<InternalState | null>(null);
  const shouldBackfillLocalTerminalRef = useRef(
    initialSource === "local" && isTerminalState(initialState),
  );
  const didBackfillLocalTerminalRef = useRef(false);

  // Repair terminal states written before the account-scoped app-state write
  // path was reliable. Same-browser localStorage still suppresses the modal;
  // this best-effort write makes that dismissal durable for the user account.
  useEffect(() => {
    if (
      didBackfillLocalTerminalRef.current ||
      !shouldBackfillLocalTerminalRef.current ||
      bootstrapTourState !== null ||
      !isTerminalState(state)
    ) {
      return;
    }

    didBackfillLocalTerminalRef.current = true;
    const terminal = state;
    void getToken().then((token) => {
      if (token) {
        void putTourState(terminal, token).catch(() => {});
      }
    });
  }, [bootstrapTourState, getToken, state]);

  // Flush any pending in_progress write on unmount.
  useEffect(() => {
    return () => {
      if (throttleTimerRef.current !== null) {
        clearTimeout(throttleTimerRef.current);
        throttleTimerRef.current = null;
        const pending = pendingInProgressRef.current;
        if (pending) {
          // Best-effort fire-and-forget on unmount; localStorage already saved.
          void getToken().then((token) => {
            if (token) {
              void putTourState(pending, token).catch(() => {});
            }
          });
        }
      }
    };
    // Only run on unmount — getToken identity is stable from useAuth.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ready) {
    return null;
  }

  /**
   * Write a terminal state (dismissed or completed):
   * 1. React state (immediate)
   * 2. localStorage fallback (preserves same-browser resilience even on write failure)
   * 3. Backend (best-effort; failure does not erase localStorage)
   */
  const writeTerminalState = (next: InternalState) => {
    // Cancel any pending in_progress timer — terminal wins.
    if (throttleTimerRef.current !== null) {
      clearTimeout(throttleTimerRef.current);
      throttleTimerRef.current = null;
      pendingInProgressRef.current = null;
    }

    setState(next);
    // Save localStorage fallback FIRST so write failure doesn't leave the
    // browser unprotected from repeated interruptions.
    saveTourState(next);

    // Best-effort backend write. getToken() resolves null with no session,
    // in which case localStorage (saved above) is the only record.
    void getToken().then((token) => {
      if (token) {
        void putTourState(next, token).catch(() => {
          // localStorage already saved above — no action needed.
        });
      }
    });
  };

  /**
   * Write an intermediate in_progress state with trailing-edge throttle.
   * Rapid Back/Next clicks produce at most one backend write per gap window.
   * localStorage is updated immediately for same-browser resilience.
   */
  const writeInProgressState = (next: InternalState) => {
    setState(next);
    // Always update localStorage immediately (same-browser fallback).
    saveTourState(next);
    pendingInProgressRef.current = next;

    // Trailing-edge throttle for backend writes.
    if (throttleTimerRef.current !== null) {
      clearTimeout(throttleTimerRef.current);
    }
    throttleTimerRef.current = setTimeout(() => {
      throttleTimerRef.current = null;
      const pending = pendingInProgressRef.current;
      pendingInProgressRef.current = null;
      if (pending) {
        void getToken().then((token) => {
          if (token) {
            void putTourState(pending, token).catch(() => {
              // localStorage already has the state; no action needed.
            });
          }
        });
      }
    }, IN_PROGRESS_WRITE_GAP_MS);
  };

  const handleNext = () => {
    const lastIndex = steps.length - 1;
    if (state.step >= lastIndex) {
      const completed: InternalState = { status: "completed", step: lastIndex };
      writeTerminalState(completed);
      clearTourSessionDismiss();
      setIsOpen(false);
      onComplete?.();
      return;
    }
    const nextStep = state.step + 1;
    writeInProgressState({ status: "in_progress", step: nextStep });
  };

  const handleBack = () => {
    if (state.step <= 0) {
      return;
    }
    writeInProgressState({ status: "in_progress", step: state.step - 1 });
  };

  const handleSkip = () => {
    markTourSessionDismissed();
    writeTerminalState({ status: "dismissed", step: state.step });
    setIsOpen(false);
  };

  const handleResume = () => {
    setIsOpen(true);
  };

  const handleDismissPill = () => {
    writeTerminalState({ status: "dismissed", step: state.step });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && isOpen) {
      handleSkip();
      return;
    }
    setIsOpen(open);
  };

  const showPill = state.status === "in_progress" && !isOpen;
  // A persisted step index was computed against whatever step list was
  // present at write time — clamp it into the CURRENT `steps` so a step
  // that disappeared between renders (code-review fix, M3 iteration 2,
  // finding 5) never indexes past the end of a shorter filtered list.
  const clampedStep = Math.min(state.step, Math.max(steps.length - 1, 0));

  return (
    <>
      {showPill ? (
        <TourResumePill
          step={clampedStep}
          steps={steps}
          onResume={handleResume}
          onDismiss={handleDismissPill}
        />
      ) : null}

      <DialogPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
        <TourModalContent
          step={clampedStep}
          steps={steps}
          onNext={handleNext}
          onBack={handleBack}
          onSkip={handleSkip}
        />
      </DialogPrimitive.Root>
    </>
  );
}

type TourModalContentProps = {
  step: number;
  steps: readonly PlatformTourStep[];
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
};

function TourModalContent({
  step,
  steps,
  onNext,
  onBack,
  onSkip,
}: TourModalContentProps) {
  const current = steps[step] ?? steps[0];
  const total = steps.length;
  const isLast = step >= total - 1;
  const isFirst = step <= 0;

  if (!current) {
    return null;
  }

  // UI-verify fix (M3 iteration 3): the eyebrow number must reflect this
  // step's POSITION in the presented list, not the hard-coded number baked
  // into `PlatformTourStep.eyebrow` (which is authored against the full,
  // unfiltered `PLATFORM_TOUR_STEPS` and goes stale the moment a step is
  // filtered out — e.g. the second presented step must read "Step 2" even
  // when it's `PLATFORM_TOUR_STEPS[2]` with eyebrow text "Step 3").
  const eyebrow = `Step ${step + 1}`;

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm" />
      <DialogPrimitive.Content
        aria-describedby={undefined}
        className={cn(
          "fixed inset-x-4 bottom-4 z-50 rounded-[var(--radius-3xl)] border border-border bg-card p-6 shadow-xl",
          "max-[640px]:pb-[max(1.5rem,env(safe-area-inset-bottom))]",
          "sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:w-full sm:max-w-lg sm:-translate-x-1/2 sm:-translate-y-1/2 sm:p-8",
        )}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <p className="eyebrow-label">{eyebrow}</p>
            <DialogPrimitive.Title className="text-2xl font-semibold tracking-[-0.02em] text-foreground">
              {current.title}
            </DialogPrimitive.Title>
          </div>
          <DialogPrimitive.Close
            aria-label="Close tour"
            className="rounded-full p-2 text-muted-foreground hover:bg-muted"
            data-slot="interactive-control"
          >
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>
        </div>

        <DialogPrimitive.Description className="body-muted mt-4 text-base leading-relaxed text-foreground/80">
          {current.body}
        </DialogPrimitive.Description>

        <div className="mt-6 flex items-center justify-center gap-2" aria-label="Tour progress">
          {steps.map((tourStep, index) => (
            <span
              key={tourStep.id}
              aria-current={index === step ? "step" : undefined}
              className={cn(
                "h-1.5 rounded-full transition-all",
                index === step ? "w-6 bg-primary" : "w-1.5 bg-border",
              )}
            />
          ))}
        </div>

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button variant="ghost" size="sm" onClick={onSkip}>
            Skip
          </Button>

          <div className="flex items-center justify-end gap-2">
            {!isFirst ? (
              <Button variant="outline" size="sm" onClick={onBack}>
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </Button>
            ) : null}
            <Button size="sm" onClick={onNext}>
              {isLast ? (
                <>
                  <Sparkles className="h-3.5 w-3.5" />
                  Finish
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="h-3.5 w-3.5" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

type TourResumePillProps = {
  step: number;
  steps?: readonly PlatformTourStep[];
  onResume: () => void;
  onDismiss: () => void;
};

export function TourResumePill({
  step,
  steps = PLATFORM_TOUR_STEPS,
  onResume,
  onDismiss,
}: TourResumePillProps) {
  const total = steps.length;
  const current = steps[step] ?? steps[0];
  const progress = total > 0 ? Math.min(((step + 1) / total) * 100, 100) : 0;

  if (!current) {
    return null;
  }

  return (
    <section
      aria-label="Resume platform tour"
      className="flex flex-col gap-3 rounded-[var(--radius-pill)] bg-card px-4 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-5"
    >
      <div className="flex items-center gap-3">
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Play className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 space-y-0.5">
          <p className="text-sm font-semibold text-foreground">
            Finish the 4-step Sill tour
          </p>
          <p className="body-muted truncate">
            <span className="font-medium text-foreground/80">
              Step {step + 1} of {total}
            </span>
            <span aria-hidden="true" className="px-1.5 text-muted-foreground">
              ·
            </span>
            <span>{current.hint}</span>
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 sm:ml-auto">
        <div
          className="hidden h-1.5 w-24 overflow-hidden rounded-full bg-border sm:block"
          aria-hidden="true"
        >
          <div
            className="h-full rounded-full bg-primary transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <Button size="sm" onClick={onResume}>
          Resume
        </Button>
        <Button size="sm" variant="ghost" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    </section>
  );
}
